import {
  ChevronDown,
  Cloud,
  CloudUpload,
  Command,
  Download,
  FileDown,
  FilePlus2,
  FolderOpen,
  Image as ImageIcon,
  LogOut,
  MonitorPlay,
  PanelRight,
  Redo2,
  Save,
  Search,
  ShieldCheck,
  Undo2,
  Upload,
  User,
  Users,
  X,
} from 'lucide-react';
import { useEditor } from '../state/store';
import { useAuth } from '../auth/AuthContext';
import { Popover } from './widgets/primitives';
import { downloadText, pickFile, readFileText } from './fileIO';

/**
 * Application chrome: identity on the left, the document's own controls in the
 * centre, and everything that leaves the app (import / export / download) on
 * the right — so the bar reads left to right as "who, what, where to".
 */
export function TopBar({
  onOpenDialog,
  onToggleSidebar,
  onOpenCommands,
  sidebarCollapsed,
}: {
  onOpenDialog: (id: string) => void;
  onToggleSidebar: () => void;
  onOpenCommands: () => void;
  sidebarCollapsed: boolean;
}): JSX.Element {
  const history = useEditor((s) => s.historyState);
  const revision = useEditor((s) => s.revision);
  const store = useEditor.getState();
  const tab = store.active();
  const { user, logout } = useAuth();

  const openProject = async () => {
    const file = await pickFile('.json,.tui.json,application/json');
    if (!file) return;
    store.loadProject(await readFileText(file), file.name);
  };

  const importAnsi = async () => {
    const file = await pickFile('.ans,.txt,.asc,.nfo,text/plain');
    if (!file) return;
    store.importAnsi(await readFileText(file), file.name);
  };

  const saveProject = () => {
    const name = tab.doc.name.trim() || 'untitled';
    downloadText(`${name}.tui.json`, store.projectJson(true), 'application/json');
    tab.dirty = false;
    store.notify(`Saved ${name}.tui.json`, 'success');
  };

  return (
    <header className="topbar" key={revision}>
      <div className="brand">
        <img className="brand-mark" src="/favicon.png" alt="" width={30} height={30} />
        <span className="brand-text">
          <b>Pixansi</b>
          <span>
            {tab.doc.width}×{tab.doc.height} · {tab.doc.colorMode}
          </span>
        </span>
      </div>

      <div className="pill-group">
        <button className="btn" onClick={() => onOpenDialog('new')} title="New document (Ctrl+N)">
          <FilePlus2 size={15} />
        </button>
        <button className="btn" onClick={openProject} title="Open project (Ctrl+O)">
          <FolderOpen size={15} />
        </button>
        <button className="btn" onClick={saveProject} title="Save project (Ctrl+S)">
          <Save size={15} />
        </button>
        <span className="divider" />
        <button
          className="btn"
          disabled={!history.canUndo}
          onClick={() => store.undo()}
          title={history.canUndo ? `Undo ${history.undoLabel} (Ctrl+Z)` : 'Nothing to undo'}
        >
          <Undo2 size={15} />
        </button>
        <button
          className="btn"
          disabled={!history.canRedo}
          onClick={() => store.redo()}
          title={history.canRedo ? `Redo ${history.redoLabel} (Ctrl+Shift+Z)` : 'Nothing to redo'}
        >
          <Redo2 size={15} />
        </button>
      </div>

      <div className="topbar-center">
        <div className="pill-group">
          <div className="size-field">
            <label htmlFor="doc-width">Width</label>
            <input
              id="doc-width"
              type="number"
              min={1}
              max={2000}
              value={tab.doc.width}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n >= 1) store.resizeDocument(n, tab.doc.height);
              }}
            />
          </div>
          <div className="size-field">
            <label htmlFor="doc-height">Height</label>
            <input
              id="doc-height"
              type="number"
              min={1}
              max={2000}
              value={tab.doc.height}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n >= 1) store.resizeDocument(tab.doc.width, n);
              }}
            />
          </div>
        </div>

        <div className="pill-group">
          <button className="btn" onClick={() => onOpenDialog('preview')} title="Terminal preview (Ctrl+P)">
            <MonitorPlay size={15} />
            Preview
          </button>
          <button className="btn" onClick={() => onOpenDialog('search')} title="Find and replace (Ctrl+F)">
            <Search size={15} />
          </button>
          <button className="btn" onClick={onOpenCommands} title="Command palette (Ctrl+Shift+P)">
            <Command size={15} />
          </button>
        </div>
      </div>

      <div className="pill-group">
        <button className="btn" onClick={importAnsi} title="Import an existing .ans file">
          <Upload size={15} />
          Import
        </button>
        <button className="btn" onClick={() => onOpenDialog('image')} title="Convert an image to ANSI">
          <ImageIcon size={15} />
          Image
        </button>
        <button className="btn" onClick={() => onOpenDialog('export')} title="Export (Ctrl+E)">
          <FileDown size={15} />
          Export
        </button>
        <span className="divider" />
        <button
          className="btn icon"
          onClick={() => onOpenDialog('cloud-open')}
          title="Open a project from your account"
        >
          <Cloud size={15} />
        </button>
        <button
          className="btn icon"
          onClick={() => onOpenDialog('cloud-save')}
          title="Save this document to your account"
        >
          <CloudUpload size={15} />
        </button>
      </div>

      <button className="btn primary tall" onClick={saveProject} title="Download the project file">
        <Download size={15} />
        Download
      </button>

      <Popover
        side="bottom"
        align="end"
        trigger={
          <button className="btn account-trigger" title={`Signed in as ${user?.username ?? ''}`}>
            <span className="account-dot">{(user?.username ?? '?').slice(0, 1).toUpperCase()}</span>
            <span className="account-name">{user?.username}</span>
            <ChevronDown size={13} />
          </button>
        }
      >
        <div className="account-pop">
          <div className="account-pop-head">
            <strong>{user?.username}</strong>
            <span>
              {user?.role === 'admin' ? 'Administrator' : 'Member'} · {user?.plan}
            </span>
          </div>
          <button onClick={() => onOpenDialog('account')}>
            <User size={14} /> Account
          </button>
          {user?.role === 'admin' ? (
            <button onClick={() => onOpenDialog('admin')}>
              <Users size={14} /> Administration
            </button>
          ) : null}
          <button onClick={() => onOpenDialog('cloud-open')}>
            <Cloud size={14} /> Cloud projects
          </button>
          <span className="account-sep" />
          <button className="danger" onClick={() => void logout()}>
            <LogOut size={14} /> Sign out
          </button>
          <span className="account-foot">
            <ShieldCheck size={12} /> Session protected
          </span>
        </div>
      </Popover>

      <button
        className={`btn icon${sidebarCollapsed ? '' : ' active'}`}
        onClick={onToggleSidebar}
        title="Toggle the right panel"
      >
        <PanelRight size={15} />
      </button>
    </header>
  );
}

/** Document tabs, shown above the canvas. */
export function TabStrip(): JSX.Element {
  const tabs = useEditor((s) => s.tabs);
  const activeTabId = useEditor((s) => s.activeTabId);
  const revision = useEditor((s) => s.revision);
  const store = useEditor.getState();

  return (
    <div className="tabs" key={revision}>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          className={`tab${tab.id === activeTabId ? ' active' : ''}`}
          onClick={() => store.setActiveTab(tab.id)}
          title={`${tab.doc.name} — ${tab.doc.width}×${tab.doc.height}`}
        >
          {tab.dirty ? <span className="dot" /> : null}
          <span>{tab.doc.name}</span>
          <span
            className="close"
            onClick={(e) => {
              e.stopPropagation();
              store.closeTab(tab.id);
            }}
          >
            <X size={12} />
          </span>
        </div>
      ))}
    </div>
  );
}
